import { useShowNotification } from '@inflearn/ds-react-next';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  API_ENDPOINT,
  type ApiResponse,
  type CourseCardItem,
  useClientApiV1CourseCourseIdLikeCreateMutation,
  useClientApiV1CourseCourseIdLikeDeleteMutation,
} from '../../resources/@types/courseSchema';

type Props = {
  onAddLikeToCourseSuccess?: () => void;
  onAddLikeToCourseError?: (error: unknown) => void;
  onRemoveLikeFromCourseSuccess?: () => void;
  onRemoveLikeFromCourseError?: (error: unknown) => void;
  invalidatedQueryKey: (string | unknown)[];
};

export const useLikes = ({
  onAddLikeToCourseSuccess,
  onAddLikeToCourseError,
  onRemoveLikeFromCourseError,
  onRemoveLikeFromCourseSuccess,
  invalidatedQueryKey = [API_ENDPOINT.CLIENT_V1_COURSE_LIKES],
}: Props) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [courseId, setCourseId] = useState(0);

  const { showNotification } = useShowNotification();

  const applyReaction = (targetCourseId: number, hasMyReaction: boolean) => {
    queryClient.setQueryData<ApiResponse<{ items: CourseCardItem[] }>>(
      invalidatedQueryKey,
      (previous) => {
        if (!previous?.data) {
          return previous;
        }

        return {
          ...previous,
          data: {
            ...previous.data,
            items: previous.data.items.map((item) =>
              item.id === targetCourseId ? { ...item, hasMyReaction } : item,
            ),
          },
        };
      },
    );
  };

  const { mutateAsync: addLikeToCourseMutate } = useClientApiV1CourseCourseIdLikeCreateMutation({
    url: API_ENDPOINT.CLIENT_V1_COURSE_COURSEID_LIKE(courseId.toString()),
    options: {
      onSuccess: () => {
        showNotification({
          type: 'success',
          title: t('좋아요에 추가되었습니다.'),
        });

        applyReaction(courseId, true);

        onAddLikeToCourseSuccess?.();
      },
      onError: (error) => {
        showNotification({
          type: 'error',
          title: t('좋아요 추가에 실패했습니다.'),
          message: t('잠시 후 다시 시도해주세요.'),
        });

        onAddLikeToCourseError?.(error);
      },
    },
  });

  const { mutateAsync: removeLikeFromCourseMutate } =
    useClientApiV1CourseCourseIdLikeDeleteMutation({
      url: API_ENDPOINT.CLIENT_V1_COURSE_COURSEID_LIKE(courseId.toString()),
      options: {
        onSuccess: () => {
          showNotification({
            type: 'success',
            title: t('좋아요에서 제거되었습니다.'),
          });

          applyReaction(courseId, false);

          onRemoveLikeFromCourseSuccess?.();
        },
        onError: (error) => {
          showNotification({
            type: 'error',
            title: t('좋아요 제거에 실패했습니다.'),
            message: t('잠시 후 다시 시도해주세요.'),
          });

          onRemoveLikeFromCourseError?.(error);
        },
      },
    });

  const addLikeToCourse = async (targetCourseId: number) => {
    setCourseId(targetCourseId);
    await addLikeToCourseMutate({});
  };

  const removeLikeFromCourse = async (targetCourseId: number) => {
    setCourseId(targetCourseId);
    await removeLikeFromCourseMutate({});
  };

  return { addLikeToCourse, removeLikeFromCourse };
};
